import React, { useEffect, useMemo, useRef } from "react";

import { useProductColors } from "../../../hooks/useProductColors";

import { ColorBottomMenuPanel, IColorBottomMenuPanelRef } from "../../bottom-menu-panels/color";
import { useBackground } from "./hooks/useBackground";
import { useSetColorBackground } from "./hooks/useSetColorBackground";
import { useUsedBackgrounds } from "./hooks/useUsedBackgrounds";
import { useIsMounted } from "../../../hooks/useIsMounted";

export const BackgroundColorPanelContainer = () => {
	// use memo?
	const panelRef = useRef<IColorBottomMenuPanelRef>(null);
	const { usedColors } = useUsedBackgrounds();
	const background = useBackground();
	const setColorBackground = useSetColorBackground();

	// Get Colors lists
	const { data: productColors = [] } = useProductColors();

	// The colors to show is a combination of different lists
	// Colors missing in the list provided by the server happens quite a lot
	const colorsToShow = useMemo<Array<eXpress.core.IColor>>(
		() =>
			usedColors
				.filter(
					(color) =>
						!productColors?.some((prdColor) => prdColor.hexColorString === color),
				) // only add colors at front that are not in the sorted palette (else distinct will move color)
				.sort() // to prevent that order changes if a zone is removed.
				.map((color) => ({
					// Map usedColors to IColor structure
					hexColorString: color,
					// We don't use colorStyle for backgrounds yet
				}))
				.concat(productColors), // Don't sort palette from server
		[productColors, usedColors],
	);

	// Scroll into view if background is changed
	const isMounted = useIsMounted();
	const index =
		background instanceof eXpress.core.ColorBackground
			? productColors.findIndex(
					(color) =>
						eXpress.HexColorString.fromString(background.color.webColor) ===
						color.hexColorString,
			  )
			: -1;

	useEffect(() => {
		if (index >= 0) {
			const scrollAnimated = isMounted();
			panelRef.current?.scrollIntoView(index, { animate: scrollAnimated });
		}
	}, [isMounted, index]);

	if (!setColorBackground) return null;

	return (
		<ColorBottomMenuPanel
			colors={colorsToShow}
			selectedColor={
				background instanceof eXpress.core.ColorBackground
					? eXpress.HexColorString.fromString(background?.color.webColor)
					: undefined
			}
			onClick={(color) => {
				setColorBackground(color.hexColorString);
			}}
			ref={panelRef}
		/>
	);
};
BackgroundColorPanelContainer.displayName = "BackgroundColorPanelContainer";
